import { resolveChartDates } from './chartDateWindow';

export type DailyExecutionMetricBucket = {
  total: number;
  completed: number;
  failed: number;
  durationSumSeconds: number;
  durationCount: number;
  flakeOutcomes: Array<'passed' | 'notPassed'>;
  flakeScore?: number | null;
};

export const createDailyExecutionMetricBucket = (): DailyExecutionMetricBucket => ({
  total: 0,
  completed: 0,
  failed: 0,
  durationSumSeconds: 0,
  durationCount: 0,
  flakeOutcomes: [],
});

const calculateFlakeScore = (outcomes: ReadonlyArray<'passed' | 'notPassed'>): number | null => {
  if (outcomes.length < 2) {
    return null;
  }

  let flips = 0;
  for (let index = 1; index < outcomes.length; index += 1) {
    if (outcomes[index] !== outcomes[index - 1]) {
      flips += 1;
    }
  }

  return (flips / (outcomes.length - 1)) * 100;
};

export const buildMetricChartSeriesFromBuckets = (
  bucketsByDate: ReadonlyMap<string, DailyExecutionMetricBucket>,
  chartDates: string[],
): {
  dates: string[];
  passRateChartData: Array<Record<string, number | string | null>>;
  frequencyChartData: Array<Record<string, number | string | null>>;
  durationChartData: Array<Record<string, number | string | null>>;
  flakeScoreChartData: Array<Record<string, number | string | null>>;
} => {
  const dates = resolveChartDates(chartDates, bucketsByDate.keys());
  let previousFlakeOutcome: 'passed' | 'notPassed' | null = null;
  const flakeScoreChartData = dates.map((label) => {
    const bucket = bucketsByDate.get(label);
    if (!bucket) {
      return { label, flakeScore: null };
    }
    if (bucket.flakeScore != null) {
      return { label, flakeScore: bucket.flakeScore };
    }
    if (bucket.flakeOutcomes.length === 0) {
      return { label, flakeScore: null };
    }

    const outcomes =
      previousFlakeOutcome === null
        ? bucket.flakeOutcomes
        : [previousFlakeOutcome, ...bucket.flakeOutcomes];
    previousFlakeOutcome = bucket.flakeOutcomes[bucket.flakeOutcomes.length - 1] ?? null;

    return {
      label,
      flakeScore: calculateFlakeScore(outcomes),
    };
  });

  return {
    dates,
    passRateChartData: dates.map((label) => {
      const bucket = bucketsByDate.get(label) ?? createDailyExecutionMetricBucket();
      return {
        label,
        passRate:
          bucket.completed + bucket.failed > 0
            ? (bucket.completed / (bucket.completed + bucket.failed)) * 100
            : null,
      };
    }),
    frequencyChartData: dates.map((label) => ({
      label,
      frequency: (bucketsByDate.get(label) ?? createDailyExecutionMetricBucket()).total,
    })),
    durationChartData: dates.map((label) => {
      const bucket = bucketsByDate.get(label) ?? createDailyExecutionMetricBucket();
      return {
        label,
        duration:
          bucket.durationCount > 0 ? bucket.durationSumSeconds / bucket.durationCount : null,
      };
    }),
    flakeScoreChartData,
  };
};

export const buildMetricSummaryFromBuckets = (
  bucketsByDate: ReadonlyMap<string, DailyExecutionMetricBucket>,
  chartDates: string[],
): {
  passRate: number | null;
  frequencyPerDay: number | null;
  avgDurationSeconds: number | null;
  flakeScore: number | null;
} => {
  const dates = resolveChartDates(chartDates, bucketsByDate.keys());
  let total = 0;
  let completed = 0;
  let failed = 0;
  let durationSumSeconds = 0;
  let durationCount = 0;
  const flakeOutcomes: Array<'passed' | 'notPassed'> = [];

  bucketsByDate.forEach((bucket) => {
    total += bucket.total;
    completed += bucket.completed;
    failed += bucket.failed;
    durationSumSeconds += bucket.durationSumSeconds;
    durationCount += bucket.durationCount;
  });
  dates.forEach((date) => {
    flakeOutcomes.push(...(bucketsByDate.get(date)?.flakeOutcomes ?? []));
  });

  const passRateDenominator = completed + failed;

  return {
    passRate: passRateDenominator > 0 ? (completed / passRateDenominator) * 100 : null,
    frequencyPerDay: dates.length > 0 ? total / dates.length : null,
    avgDurationSeconds: durationCount > 0 ? durationSumSeconds / durationCount : null,
    flakeScore: calculateFlakeScore(flakeOutcomes),
  };
};

export const buildStatusChartRows = <TStatus extends string>(
  countsByDate: ReadonlyMap<string, Record<TStatus, number>>,
  visibleStatuses: TStatus[],
  chartDates: string[],
): Array<Record<string, string | number>> => {
  const dates = resolveChartDates(chartDates, countsByDate.keys());

  return dates.map((label) =>
    visibleStatuses.reduce<Record<string, string | number>>(
      (acc, status) => ({
        ...acc,
        [status]: countsByDate.get(label)?.[status] ?? 0,
      }),
      { label },
    ),
  );
};
